import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { LayoutChangeEvent } from 'react-native';
import {
  ArrowNavigationOptions,
  ArrowNavigationResult,
  ScrollableListComponent,
} from './useArrowNavigation.types';

export default function useArrowNavigation<T>(
  listRef: RefObject<ScrollableListComponent<T>>,
  scrollOffsetRef: MutableRefObject<number>,
  contentWidthRef: MutableRefObject<number>,
  options: ArrowNavigationOptions,
): ArrowNavigationResult {
  const { containerWidth, contentWidth, itemWidth, showArrows, itemsCount } =
    options;

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isLeftArrowHovered, setIsLeftArrowHovered] = useState(false);
  const [isRightArrowHovered, setIsRightArrowHovered] = useState(false);
  const [localContainerWidth, setLocalContainerWidth] =
    useState(containerWidth);
  const [localContentWidth, setLocalContentWidth] = useState(contentWidth);

  const updateArrowVisibility = useCallback(() => {
    if (!showArrows) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }

    const currentOffset = scrollOffsetRef.current || 0;
    const shouldShowLeftArrow = currentOffset > 10;

    if (showLeftArrow !== shouldShowLeftArrow) {
      setIsLeftArrowHovered(false);
    }
    setShowLeftArrow(shouldShowLeftArrow);

    const currentContentWidth = contentWidthRef.current || localContentWidth;
    if (currentContentWidth > 0 && localContainerWidth > 0) {
      const isAtEnd =
        currentOffset + localContainerWidth >= currentContentWidth - 20;
      const shouldShowRightArrow =
        !isAtEnd && currentContentWidth > localContainerWidth && itemsCount > 0;

      if (showRightArrow !== shouldShowRightArrow) {
        setIsRightArrowHovered(false);
      }
      setShowRightArrow(shouldShowRightArrow);
    }
  }, [
    showArrows,
    localContentWidth,
    localContainerWidth,
    itemsCount,
    scrollOffsetRef,
    contentWidthRef,
    showLeftArrow,
    showRightArrow,
  ]);

  useEffect(() => {
    updateArrowVisibility();
  }, [showArrows, updateArrowVisibility]);

  const handleArrowForward = useCallback(() => {
    if (listRef.current) {
      const currentOffset = scrollOffsetRef.current || 0;
      const scrollAmount = Math.min(localContainerWidth * 0.8, itemWidth * 3);
      const newOffset = currentOffset + scrollAmount;
      listRef.current.scrollToOffset({
        offset: newOffset,
        animated: true,
      });
    }
  }, [localContainerWidth, itemWidth, listRef, scrollOffsetRef]);

  const handleArrowBack = useCallback(() => {
    if (listRef.current) {
      const currentOffset = scrollOffsetRef.current || 0;
      const scrollAmount = Math.min(localContainerWidth * 0.8, itemWidth * 3);
      const newOffset = Math.max(0, currentOffset - scrollAmount);
      listRef.current.scrollToOffset({
        offset: newOffset,
        animated: true,
      });
    }
  }, [localContainerWidth, itemWidth, listRef, scrollOffsetRef]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setLocalContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const handleContentSizeChange = useCallback(
    (width: number) => {
      if (width !== localContentWidth) {
        setLocalContentWidth(width);
        if (contentWidthRef.current !== undefined) {
          contentWidthRef.current = width;
        }
        updateArrowVisibility();
      }
    },
    [localContentWidth, updateArrowVisibility, contentWidthRef],
  );

  const handleLeftArrowMouseEnter = useCallback(() => {
    setIsLeftArrowHovered(true);
  }, []);

  const handleLeftArrowMouseLeave = useCallback(() => {
    setIsLeftArrowHovered(false);
  }, []);

  const handleRightArrowMouseEnter = useCallback(() => {
    setIsRightArrowHovered(true);
  }, []);

  const handleRightArrowMouseLeave = useCallback(() => {
    setIsRightArrowHovered(false);
  }, []);

  return {
    showLeftArrow,
    showRightArrow,
    isLeftArrowHovered,
    isRightArrowHovered,
    updateArrowVisibility,
    handleArrowForward,
    handleArrowBack,
    handleLayout,
    handleContentSizeChange,
    handleLeftArrowMouseEnter,
    handleLeftArrowMouseLeave,
    handleRightArrowMouseEnter,
    handleRightArrowMouseLeave,
  };
}
